'use client';

import clsx from 'clsx';
import { useId } from 'react';
import Icon from '@publishing/components/ui/Icon';
import { ActionCheck, ActionCheckType2 } from '@publishing/components/ui/SvgSprite';
import styles from './Checkbox.module.scss';

export type CheckboxVariant = 'default' | 'minimal';

/** 체크 박스·아이콘 크기 — `Checkbox.module.scss`의 `size_*` 클래스 + `CHECKBOX_ICON_SIZE` */
export type CheckboxSize = 'lg' | 'md' | 'sm';

/** `Checkbox.module.scss`의 `.size_sm` / `.size_md` / `.size_lg` */
const CHECKBOX_SIZE_CLASS: Record<CheckboxSize, string> = {
  sm: styles.size_sm,
  md: styles.size_md,
  lg: styles.size_lg,
};

const CHECKBOX_ICON_SIZE: Record<CheckboxSize, number> = {
  sm: 16,
  md: 20,
  lg: 24,
};

/** `Checkbox.module.scss`의 `.type_default` / `.type_minimal` */
const CHECKBOX_VARIANT_CLASS: Record<CheckboxVariant, string> = {
  default: styles.type_default,
  minimal: styles.type_minimal,
};

export interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  name?: string;
  value?: string;
  /** default: 박스형 / minimal: 배경·테두리 없이 체크 마크만 */
  variant?: CheckboxVariant;
  /** 박스·아이콘 크기 — `Checkbox.module.scss`의 `.size_sm` / `.size_md` / `.size_lg` */
  size?: CheckboxSize;
  /** 비선택 시 라벨 회색 → 체크 시 검정(또는 `type_minimal`+`size="sm"`이면 파랑)으로 같이 전환 */
  highlight?: boolean;
  caption?: string;
  /** `true`이면 루트가 `span`이며 박스·마크만 렌더 (외부 `<label htmlFor={id}>`와 조합) */
  embedded?: boolean;
  /** `embedded` 또는 외부 연동 시 `input` / `label` `htmlFor`용 id */
  id?: string;
  className?: string;
  labelClassName?: string;
}

export default function Checkbox({
  checked,
  defaultChecked,
  onChange,
  label,
  disabled = false,
  name,
  value,
  variant = 'default',
  size = 'md',
  highlight = false,
  className,
  caption,
  embedded = false,
  id: idProp,
  labelClassName,
}: CheckboxProps) {
  const variantClass = CHECKBOX_VARIANT_CLASS[variant];
  const sizeClass = CHECKBOX_SIZE_CLASS[size];
  const highlightClass = highlight ? styles.is_highlight : '';
  const checkIconName = variant === 'default' ? ActionCheckType2 : ActionCheck;
  const generatedId = useId();
  const inputId = idProp ?? generatedId;
  const isControlled = checked !== undefined;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.checked);
  };

  const wrapperClass = clsx(styles.wrapper, embedded && styles.wrapper_embedded, sizeClass, variantClass, highlightClass, caption && styles.with_caption, disabled && styles.disabled, className);

  const control = (
    <span className={styles.box}>
      <input
        id={inputId}
        type="checkbox"
        name={name}
        value={value}
        checked={isControlled ? checked : undefined}
        defaultChecked={!isControlled ? defaultChecked : undefined}
        disabled={disabled}
        onChange={handleChange}
        className={styles.input}
      />
      <span className={styles.mark} aria-hidden>
        <span className={styles.markSymbol}>
          <Icon name={checkIconName} size={CHECKBOX_ICON_SIZE[size]} />
        </span>
      </span>
    </span>
  );

  if (embedded) {
    return <span className={wrapperClass}>{control}</span>;
  }

  return (
    <label htmlFor={inputId} className={wrapperClass}>
      {control}
      {caption ? (
        <div className={styles.labelBlock}>
          {label && (
            <span className={clsx(styles.label, labelClassName)}>
              {label}
            </span>
          )}
          <span className={styles.caption}>{caption}</span>
        </div>
      ) : (
        label && (
          <span className={clsx(styles.label, labelClassName)}>
            {label}
          </span>
        )
      )}
    </label>
  );
}
